import React, { useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, ShieldCheck, CheckCircle2, AlertTriangle, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";

/*
 * Safe bulk-update dialog for the Accounts page.
 *
 *   Step 1  Upload  →  server parses the workbook, returns column headers + suggested mapping
 *   Step 2  Map     →  user picks which source column feeds which target field
 *   Step 3  Preview →  server runs dry_run, returns per-row diff (before → after)
 *   Step 4  Apply   →  admin-only, writes the exact same diff to the DB
 *
 * The server refuses any target field that isn't on ALLOWED_ACCOUNT_UPDATE_FIELDS so
 * linked data (deals, projects, tickets, contacts, portal, cyber posture) is never
 * touched — only the mapped account columns move.
 */
export default function BulkImportDialog({ open, onOpenChange, onApplied }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null); // { row_count, columns, suggested_field_mapping, allowed_target_fields, sample_rows }
  const [mapping, setMapping] = useState({});  // { target_field: source_column }
  const [dealMapping, setDealMapping] = useState({}); // deal field → source column
  const [dealNameColumn, setDealNameColumn] = useState("");
  const [importDeals, setImportDeals] = useState(true);
  const [createMissing, setCreateMissing] = useState(true);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null); // { summary, results } from dry_run
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null); // summary after apply
  const [step, setStep] = useState(1);
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null);
    setParsed(null);
    setMapping({});
    setDealMapping({});
    setDealNameColumn("");
    setImportDeals(true);
    setPreview(null);
    setApplied(null);
    setStep(1);
    if (fileRef.current) fileRef.current.value = "";
  };
  const closeAll = () => { reset(); onOpenChange(false); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParsed(null);
    setPreview(null);
    setApplied(null);
  };

  const parse = async () => {
    if (!file) return toast.error("Choose a file first.");
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/accounts/bulk-import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(r.data);
      setMapping(r.data.suggested_field_mapping || {});
      setDealMapping(r.data.suggested_deal_field_mapping || {});
      setDealNameColumn(r.data.deal_name_column_guess || "");
      setStep(2);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Could not parse file";
      // Show a longer toast so we can actually read the underlying error
      toast.error(String(msg).slice(0, 300), { duration: 12000 });
    } finally {
      setParsing(false);
    }
  };

  const mappedCount = useMemo(
    () => Object.values(mapping || {}).filter(Boolean).length,
    [mapping]
  );
  const dealMappedCount = useMemo(
    () => Object.values(dealMapping || {}).filter(Boolean).length,
    [dealMapping]
  );
  const canPreview = mappedCount > 0 || (importDeals && dealNameColumn && dealMappedCount > 0);

  const buildOptions = (mode) => ({
    mode,
    match_by: "name",
    create_missing: createMissing,
    field_mapping: Object.fromEntries(Object.entries(mapping).filter(([, v]) => !!v)),
    ...(importDeals && dealNameColumn
      ? {
          deal_name_column: dealNameColumn,
          deal_field_mapping: Object.fromEntries(Object.entries(dealMapping).filter(([, v]) => !!v)),
        }
      : {}),
  });

  const runDryRun = async () => {
    if (!canPreview) return toast.error("Map at least one field (or enable deals with a deal-name column + at least one deal field) before previewing.");
    setRunning(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("options_json", JSON.stringify(buildOptions("dry_run")));
      const r = await api.post("/accounts/bulk-import/run", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setRunning(false);
    }
  };

  const apply = async () => {
    if (!preview) return;
    const s = preview.summary;
    const dealsLine = s.deals_enabled
      ? `\nDeals: ${s.deal_update_count} update · ${s.deal_create_count} new`
      : "";
    if (!window.confirm(
      `Apply changes?\n\nAccounts: ${s.update_count} update · ${s.create_count} new${dealsLine}\n\nProjects, tickets, contacts, portal settings and cyber posture are NOT touched. Existing deals not in the file are kept.`
    )) return;
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("options_json", JSON.stringify(buildOptions("apply")));
      const r = await api.post("/accounts/bulk-import/run", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setApplied(r.data.summary);
      setStep(4);
      const ds = r.data.summary.deals_enabled
        ? ` · deals ${r.data.summary.deal_applied_updated}u/${r.data.summary.deal_applied_created}c`
        : "";
      toast.success(`Applied · accounts ${r.data.summary.applied_updated}u/${r.data.summary.applied_created}c${ds}`);
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const setMap = (target, col) => setMapping((m) => ({ ...m, [target]: col === "__none__" ? "" : col }));

  const changesTotal = preview?.results?.reduce((n, r) => n + Object.keys(r.changes || {}).length, 0) || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : closeAll())}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" /> Bulk update accounts
          </DialogTitle>
          <DialogDescription>
            Uploads never touch linked data. Projects, deals, tickets, contacts, portal access,
            cyber posture uploads and Xero links are protected on the server — only the fields
            you explicitly map can move.
          </DialogDescription>
        </DialogHeader>

        {/* Step banner */}
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider" data-testid="bulk-import-steps">
          {["Upload", "Map fields", "Preview", "Done"].map((label, i) => {
            const n = i + 1;
            const done = step > n;
            const current = step === n;
            return (
              <React.Fragment key={label}>
                <div className={`px-2 py-1 rounded ${current ? "bg-blue-600 text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {n}. {label}
                </div>
                {n < 4 && <ArrowRight className="h-3 w-3 text-slate-300" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* --- Step 1: Upload --- */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-xs text-slate-700 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-700 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-blue-900 mb-0.5">Safe mode</div>
                Only fields you explicitly map get updated. Everything else on each account —
                including linked deals, projects, tickets, contacts, portal invites, cyber posture
                uploads and ChartMogul / Xero links — is left completely alone.
              </div>
            </div>
            <div>
              <Label>Choose a workbook (xlsx / xls / csv)</Label>
              <Input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={onFile}
                data-testid="bulk-import-file"
              />
              {file && <div className="text-xs text-slate-500 mt-1">Selected: {file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeAll}>Cancel</Button>
              <Button
                onClick={parse}
                disabled={!file || parsing}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="bulk-import-parse-btn"
              >
                {parsing ? "Reading…" : "Read file"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* --- Step 2: Map fields --- */}
        {step === 2 && parsed && (
          <div className="space-y-4">
            <div className="text-xs text-slate-500">
              Detected <span className="font-semibold text-slate-700">{parsed.row_count}</span> rows and{" "}
              <span className="font-semibold text-slate-700">{parsed.columns.length}</span> columns.
              Account name column: <span className="font-mono text-blue-700">{parsed.account_column_guess || "(none detected)"}</span>
            </div>

            <div className="rounded-lg border border-slate-200">
              <div className="px-3 py-2 border-b bg-slate-50 text-[10px] font-mono uppercase tracking-wider text-slate-500 grid grid-cols-2 gap-3">
                <div>Target account field</div>
                <div>Source column in your file</div>
              </div>
              <div className="divide-y divide-slate-100">
                {(parsed.allowed_target_fields || []).map((f) => (
                  <div key={f.key} className="grid grid-cols-2 gap-3 items-center px-3 py-2">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{f.label}</div>
                      <div className="text-[10px] font-mono text-slate-400">{f.key} · {f.type}</div>
                    </div>
                    <Select
                      value={mapping[f.key] || "__none__"}
                      onValueChange={(v) => setMap(f.key, v)}
                    >
                      <SelectTrigger data-testid={`bulk-import-map-${f.key}`}>
                        <SelectValue placeholder="— skip this field —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— skip this field —</SelectItem>
                        {parsed.columns.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={createMissing}
                onChange={(e) => setCreateMissing(e.target.checked)}
                className="h-4 w-4 accent-blue-600"
                data-testid="bulk-import-create-missing"
              />
              Create new accounts when a row doesn&apos;t match any existing account (matched by exact name)
            </label>

            {/* Deals mapping (opt-in) */}
            <div className="rounded-lg border border-slate-200">
              <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-heading font-semibold text-slate-800">
                  <input
                    type="checkbox"
                    checked={importDeals}
                    onChange={(e) => setImportDeals(e.target.checked)}
                    className="h-4 w-4 accent-blue-600"
                    data-testid="bulk-import-enable-deals"
                  />
                  Also import deals from this file (one deal per row)
                </label>
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  Existing deals not in the file are kept
                </span>
              </div>
              {importDeals && (
                <div className="p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3 items-center">
                    <div>
                      <div className="text-sm font-medium text-slate-800">Deal name column *</div>
                      <div className="text-[10px] font-mono text-slate-400">Matches deals per account by name</div>
                    </div>
                    <Select
                      value={dealNameColumn || "__none__"}
                      onValueChange={(v) => setDealNameColumn(v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger data-testid="bulk-import-deal-name-col">
                        <SelectValue placeholder="— pick a column —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— skip deal import —</SelectItem>
                        {parsed.columns.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="divide-y divide-slate-100 border-t border-slate-100">
                    {(parsed.allowed_deal_target_fields || []).map((f) => (
                      <div key={f.key} className="grid grid-cols-2 gap-3 items-center py-2">
                        <div>
                          <div className="text-sm text-slate-700">{f.label}</div>
                          <div className="text-[10px] font-mono text-slate-400">deal.{f.key} · {f.type}</div>
                        </div>
                        <Select
                          value={dealMapping[f.key] || "__none__"}
                          onValueChange={(v) => setDealMapping((m) => ({ ...m, [f.key]: v === "__none__" ? "" : v }))}
                        >
                          <SelectTrigger data-testid={`bulk-import-deal-map-${f.key}`}>
                            <SelectValue placeholder="— skip —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— skip —</SelectItem>
                            {parsed.columns.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2">
                    Stage values like <span className="font-mono">Renewal Verbal Confirmation</span>,{" "}
                    <span className="font-mono">Proposal Submitted</span>, <span className="font-mono">Webinar/Demo</span>{" "}
                    are mapped automatically to the closest Xentra stage; renewal-flavoured stages set{" "}
                    <span className="font-mono">subscription_type = Renewal</span>.
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button
                onClick={runDryRun}
                disabled={!canPreview || running}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="bulk-import-preview-btn"
              >
                {running ? "Building preview…" : `Preview ${mappedCount} account field${mappedCount === 1 ? "" : "s"}${importDeals && dealNameColumn ? ` + ${dealMappedCount} deal field${dealMappedCount === 1 ? "" : "s"}` : ""}`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* --- Step 3: Preview --- */}
        {step === 3 && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <SummaryCard label="Rows in file" value={preview.summary.total_source_accounts} tone="slate" />
              <SummaryCard label="Will update" value={preview.summary.update_count} tone="blue" testid="bulk-import-summary-updates" />
              <SummaryCard label="Will create" value={preview.summary.create_count} tone="emerald" testid="bulk-import-summary-creates" />
              <SummaryCard label="Unchanged" value={preview.summary.unchanged_count} tone="slate" testid="bulk-import-summary-unchanged" />
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-xs text-emerald-900 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-0.5">Protected by the server</div>
                These fields are never touched by this import: {preview.summary.protected_fields.join(", ")}.
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Account</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-left px-3 py-2">Changes ({changesTotal} field{changesTotal === 1 ? "" : "s"})</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.results.slice(0, 500).map((r, i) => (
                    <tr key={i} className="border-t align-top" data-testid={`bulk-import-row-${i}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.account_name}</td>
                      <td className="px-3 py-2">
                        <ActionBadge action={r.action} />
                      </td>
                      <td className="px-3 py-2">
                        {Object.keys(r.changes || {}).length === 0 ? (
                          <span className="text-slate-400 text-xs">No changes</span>
                        ) : (
                          <div className="space-y-1">
                            {Object.entries(r.changes).map(([field, diff]) => (
                              <div key={field} className="text-xs flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">{field}</span>
                                <span className="text-red-600 line-through decoration-red-400/60">{fmtCell(diff.before)}</span>
                                <ArrowRight className="h-3 w-3 text-slate-400" />
                                <span className="text-emerald-700 font-semibold">{fmtCell(diff.after)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.results.length > 500 && (
                <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-t">
                  Showing 500 of {preview.results.length} rows — apply will process all of them.
                </div>
              )}
            </div>

            {/* Deal preview section (only when deals were imported) */}
            {preview.summary.deals_enabled && (
              <>
                <div className="grid grid-cols-3 gap-3 pt-2">
                  <SummaryCard label="Deals · will update" value={preview.summary.deal_update_count} tone="blue" testid="bulk-import-summary-deal-updates" />
                  <SummaryCard label="Deals · will create" value={preview.summary.deal_create_count} tone="emerald" testid="bulk-import-summary-deal-creates" />
                  <SummaryCard label="Deals · unchanged" value={preview.summary.deal_unchanged_count} tone="slate" testid="bulk-import-summary-deal-unchanged" />
                </div>
                {preview.summary.deal_disambiguated_count > 0 && (
                  <div
                    className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                    data-testid="bulk-import-disambig-banner"
                  >
                    <span className="font-mono uppercase text-[10px] tracking-wider bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 text-amber-800 shrink-0">
                      {preview.summary.deal_disambiguated_count} auto-renamed
                    </span>
                    <span>
                      Your file has {preview.summary.deal_disambiguated_count} duplicate deal-name row(s) under the same account (same product name across multiple close dates).
                      To keep every row we appended the close date to the duplicates (e.g. <code className="bg-white px-1 rounded">Boxphish - 12 Months · 2025-03-31</code>). You can rename them later if you prefer.
                    </span>
                  </div>
                )}
                <div className="rounded-lg border border-slate-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2">Account</th>
                        <th className="text-left px-3 py-2">Deal</th>
                        <th className="text-left px-3 py-2">Action</th>
                        <th className="text-left px-3 py-2">Changes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.deal_results || []).slice(0, 500).map((r, i) => (
                        <tr key={i} className="border-t align-top" data-testid={`bulk-import-deal-row-${i}`}>
                          <td className="px-3 py-2 text-slate-700">{r.account_name}</td>
                          <td className="px-3 py-2 font-medium text-slate-800">{r.deal_name}</td>
                          <td className="px-3 py-2"><ActionBadge action={r.action} /></td>
                          <td className="px-3 py-2">
                            {Object.keys(r.changes || {}).length === 0 ? (
                              <span className="text-slate-400 text-xs">No changes</span>
                            ) : (
                              <div className="space-y-1">
                                {Object.entries(r.changes).map(([field, diff]) => (
                                  <div key={field} className="text-xs flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">{field}</span>
                                    <span className="text-red-600 line-through decoration-red-400/60">{fmtCell(diff.before)}</span>
                                    <ArrowRight className="h-3 w-3 text-slate-400" />
                                    <span className="text-emerald-700 font-semibold">{fmtCell(diff.after)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(preview.deal_results || []).length > 500 && (
                    <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-t">
                      Showing 500 of {preview.deal_results.length} deal rows — apply will process all of them.
                    </div>
                  )}
                </div>
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(2)}>Back to mapping</Button>
              <Button
                onClick={apply}
                disabled={applying || (preview.summary.update_count === 0 && preview.summary.create_count === 0 && preview.summary.deal_update_count === 0 && preview.summary.deal_create_count === 0)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="bulk-import-apply-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {applying ? "Applying…" : `Apply ${preview.summary.update_count} update${preview.summary.update_count === 1 ? "" : "s"}${preview.summary.create_count > 0 ? ` + ${preview.summary.create_count} new` : ""}${preview.summary.deals_enabled ? ` · deals ${preview.summary.deal_update_count}u/${preview.summary.deal_create_count}c` : ""}`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* --- Step 4: Done --- */}
        {step === 4 && applied && (
          <div className="space-y-4" data-testid="bulk-import-done">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-700 mt-0.5" />
              <div>
                <div className="font-heading font-semibold text-emerald-900">Import applied</div>
                <div className="text-sm text-emerald-800 mt-1">
                  {applied.applied_updated} account{applied.applied_updated === 1 ? "" : "s"} updated ·{" "}
                  {applied.applied_created} account{applied.applied_created === 1 ? "" : "s"} created ·{" "}
                  {applied.unchanged_count} unchanged.
                </div>
                {applied.deals_enabled && (
                  <div className="text-sm text-emerald-800 mt-1">
                    Deals: {applied.deal_applied_updated} updated · {applied.deal_applied_created} created ·{" "}
                    {applied.deal_unchanged_count} unchanged.
                  </div>
                )}
                <div className="text-xs text-emerald-700 mt-2">
                  Projects, tickets, contacts, portal access, cyber posture uploads
                  and Xero/ChartMogul links were preserved. Existing deals not in the file were kept.
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset}>Import another file</Button>
              <Button onClick={closeAll} className="bg-blue-600 hover:bg-blue-700">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function fmtCell(v) {
  if (v == null || v === "") return <span className="text-slate-400 italic">empty</span>;
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}

function ActionBadge({ action }) {
  const map = {
    update: "bg-blue-100 text-blue-800",
    create: "bg-emerald-100 text-emerald-800",
    unchanged: "bg-slate-100 text-slate-500",
    skip: "bg-amber-100 text-amber-800",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${map[action] || map.unchanged}`}>
      {action}
    </span>
  );
}

function SummaryCard({ label, value, tone = "slate", testid }) {
  const map = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div className={`rounded-lg border p-3 ${map[tone] || map.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-mono opacity-70">{label}</div>
      <div className="text-2xl font-heading font-bold mt-1">{value}</div>
    </div>
  );
}
